import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  // Surfaces a clear message in the console if env vars are missing at build time
  console.error('Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY');
}

export const supabase = createClient(supabaseUrl, supabaseKey);

// ---------------------------------------------------------------------------
// Mapping between the DB (snake_case) and the app's existing shape (camelCase).
// Keeps `_id` alive so the rest of the UI keeps working unchanged.
// ---------------------------------------------------------------------------
export const fromRow = (r) => r && ({
  _id: r.id,
  id: r.id,
  legacyId: r.legacy_id,
  guardianName: r.guardian_name,
  guardianEmail: r.guardian_email,
  guardianPhone: r.guardian_phone,
  guardianRelation: r.guardian_relation,
  otherRelationship: r.other_relationship,
  guardianAddress: r.guardian_address,
  childName: r.child_name,
  childBirthday: r.child_birthday,
  childAge: r.child_age,
  branchLocation: r.branch_location,
  appointmentDate: r.appointment_date,
  selectedTime: r.selected_time,
  selectedProfessional: r.selected_professional,
  serviceType: r.service_type,
  paymentStatus: r.payment_status,
  paymentAmount: r.payment_amount,
  paymentMethod: r.payment_method,
  paymentReference: r.payment_reference,
  paymentDate: r.payment_date,
  accountName: r.account_name,
  status: r.status,
  adminNotes: r.admin_notes,
  rescheduleHistory: r.reschedule_history || [],
  createdAt: r.created_at,
  updatedAt: r.updated_at,
});

const COLUMN_MAP = {
  guardianName: 'guardian_name',
  guardianEmail: 'guardian_email',
  guardianPhone: 'guardian_phone',
  guardianRelation: 'guardian_relation',
  otherRelationship: 'other_relationship',
  guardianAddress: 'guardian_address',
  childName: 'child_name',
  childBirthday: 'child_birthday',
  childAge: 'child_age',
  branchLocation: 'branch_location',
  appointmentDate: 'appointment_date',
  selectedTime: 'selected_time',
  selectedProfessional: 'selected_professional',
  serviceType: 'service_type',
  paymentStatus: 'payment_status',
  paymentAmount: 'payment_amount',
  paymentMethod: 'payment_method',
  paymentReference: 'payment_reference',
  paymentDate: 'payment_date',
  accountName: 'account_name',
  status: 'status',
  adminNotes: 'admin_notes',
  rescheduleHistory: 'reschedule_history',
};

// camelCase app object -> snake_case DB columns (only mapped, defined keys)
export const toRow = (o) => {
  const out = {};
  Object.keys(o || {}).forEach((k) => {
    if (COLUMN_MAP[k] && o[k] !== undefined) out[COLUMN_MAP[k]] = o[k];
  });
  return out;
};

// Empty date strings must become null (Postgres date columns reject '')
const cleanForInsert = (row) => {
  const out = { ...row };
  ['child_birthday', 'appointment_date', 'payment_date'].forEach((c) => {
    if (out[c] === '' ) out[c] = null;
  });
  return out;
};

// ---------------------------------------------------------------------------
// Bookings data access
// ---------------------------------------------------------------------------
export async function listBookings() {
  const { data, error } = await supabase
    .from('bookings')
    .select('*')
    .order('appointment_date', { ascending: false });
  if (error) throw error;
  return (data || []).map(fromRow);
}

// Generate a UUID (secure-context browsers have crypto.randomUUID; fallback for older)
function makeId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Public/guest booking creation. RLS lets anon INSERT but NOT SELECT, so we
// supply the id client-side instead of reading the inserted row back.
export async function insertBooking(obj) {
  const id = makeId();
  const row = cleanForInsert(toRow(obj));
  row.id = id;
  const { error } = await supabase.from('bookings').insert(row);
  if (error) throw error;
  return { ...obj, _id: id, id };
}

// Admin-only (RLS enforces is_admin())
export async function updateBooking(id, patch) {
  const { data, error } = await supabase
    .from('bookings')
    .update(cleanForInsert(toRow(patch)))
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return fromRow(data);
}

export async function deleteBooking(id) {
  const { error } = await supabase.from('bookings').delete().eq('id', id);
  if (error) throw error;
}

// Guest payment submission (anon-safe RPC — updates only payment fields by id)
export async function submitPayment(id, { method, status, reference, accountName, amount = 2000, paymentDate }) {
  const { data, error } = await supabase.rpc('submit_payment', {
    p_id: id,
    p_method: method,
    p_status: status,
    p_reference: reference,
    p_account_name: accountName,
    p_amount: amount,
    p_payment_date: paymentDate || new Date().toISOString(),
  });
  if (error) throw error;
  return Array.isArray(data) ? fromRow(data[0]) : fromRow(data);
}

// Available time slots for a date+branch (anon-safe RPC, no personal data)
const ALL_SLOTS = ['8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'];
export async function getAvailableSlots(date, branch) {
  const { data, error } = await supabase.rpc('get_taken_slots', { p_date: date, p_branch: branch });
  if (error) throw error;
  const taken = new Set((data || []).map((r) => r.selected_time));
  return ALL_SLOTS.filter((s) => !taken.has(s));
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
async function profileFor(userId) {
  const { data } = await supabase
    .from('profiles')
    .select('role, first_name, last_name, phone')
    .eq('id', userId)
    .single();
  return data || {};
}

export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  const prof = await profileFor(data.user.id);
  return {
    id: data.user.id,
    email: data.user.email,
    role: prof.role || 'user',
    firstName: prof.first_name || '',
    lastName: prof.last_name || '',
    phone: prof.phone || '',
  };
}

export async function signUp(email, password) {
  const { data, error } = await supabase.auth.signUp({ email, password });
  if (error) throw error;
  return {
    id: data.user?.id,
    email,
    role: 'user',
    firstName: '',
    lastName: '',
  };
}

export async function signOut() {
  await supabase.auth.signOut();
}

// Returns the current signed-in user (with role) or null
export async function getCurrentUser() {
  const { data } = await supabase.auth.getSession();
  const user = data?.session?.user;
  if (!user) return null;
  const prof = await profileFor(user.id);
  return {
    id: user.id,
    email: user.email,
    role: prof.role || 'user',
    firstName: prof.first_name || '',
    lastName: prof.last_name || '',
    phone: prof.phone || '',
  };
}
